/*
 * 返回导航状态机 —— 三端唯一算法真源（web 这份是原本，Android
 * `views/FacadeLogic.kt` / iOS `LaneChatUICore/Facade/FacadeLogic.swift` 的
 * facadeGo/facadePop/facadeBack 是同一张表的移植）。
 *
 * 为什么要有"来路"这个显式记录 ——
 *   旧实现（handleListButtonClick / Android onListButton / iOS onListButton）
 *   是「按当前配置猜返回目标」：home_page_enabled 为真就回 Home，否则回会话
 *   列表。这在"从会话列表点进某条会话"时必然跳错：用户从列表来，按返回却被
 *   甩到 Home（或者反过来，从 Home 来却掉进列表）。返回目标是**来路的属性**，
 *   不是当前页的属性，靠当前页 + 配置推不出来，只能记。
 *
 * 记法：originStack —— 一条"回退路径"，栈底是最早的来路。
 *   · navGo(state, target)：target 不在栈里 → 把当前视图压栈；target 已经在
 *     栈里 → 说明这次"前进"其实是回到来过的地方，把栈截断到它之前（否则
 *     home→chat→home→chat 会把栈撑成无限长，返回时来回鬼打墙）。
 *     这条截断规则同时保证栈里每个视图至多出现一次，栈天然有界（≤4），
 *     不需要人为设上限。
 *   · navBack(state)：弹栈；**栈空 = 没有来路 = 关闭整个聊天**（返回 null）。
 *     "商户没开 Home、访客直接落在会话里 → 返回即关闭"就是栈空这一条，不是
 *     再判一次 home_page_enabled —— 判据只在落地/导航时用一次，之后由栈负责。
 *
 * 纯函数、零依赖（不 import 任何东西），可被 node --test 直接加载。
 */

/** 顶层视图标识（web widgetClass / native FacadeView 的公共词汇表）。 */
export const NAV_HOME = 'home';
export const NAV_CHAT = 'chat';
export const NAV_CHAT_LIST = 'chatList';
export const NAV_KNOWLEDGE = 'knowledge';

export const NAV_VIEWS = [NAV_HOME, NAV_CHAT, NAV_CHAT_LIST, NAV_KNOWLEDGE];

/**
 * 落地态。
 *
 * **Home 一旦启用，它就是导航树的根**，无论访客这次有没有实际经过它。
 * 所以落地在非 Home 视图时（回访续聊直接进会话、推送点击进会话、
 * initial_view 配成 chat …）要把 Home 预置为栈底，返回才回得去。
 *
 * 第一版只写了"落地 = 来路为空"，于是"开了 Home 但这次没从 Home 进来"
 * 按返回直接退出 —— Home 明明在那儿却回不去。栈只记录**这次会话走过的路**，
 * 而 Home 是**始终在那里的根**，两者不是一回事，得分开表达。
 *
 * homeEnabled 为假时行为不变：栈空 → 返回 = 关闭（商户没开 Home 的常态）。
 *
 * @param {string} view 这次落地的视图
 * @param {boolean} homeEnabled 商户是否启用了 Home（判据不在这里算，由调用方传入）
 * @returns {{view: string, originStack: string[]}}
 */
export function navInit(view, homeEnabled) {
    if (homeEnabled && view !== NAV_HOME) {
        return { view, originStack: [NAV_HOME] };
    }
    return { view, originStack: [] };
}

/**
 * 前进一步，显式记录来路。
 * @param {{view: string, originStack: string[]}} state
 * @param {string} target
 * @returns {{view: string, originStack: string[]}} 新状态（原状态不被改动）
 */
export function navGo(state, target) {
    const cur = state && state.view;
    const stack = (state && state.originStack) || [];
    if (target === cur) return { view: cur, originStack: stack.slice() };
    const seen = stack.indexOf(target);
    if (seen >= 0) {
        // 回到栈里已有的视图 —— 这是"往回走"，不是新增一层来路。
        return { view: target, originStack: stack.slice(0, seen) };
    }
    return { view: target, originStack: stack.concat([cur]) };
}

/**
 * 返回目标（不改状态，供 header 返回按钮决定"渲不渲染 + 用哪个图标"）。
 * @param {{view: string, originStack: string[]}} state
 * @returns {string|null} 目标视图；null = 没有来路（返回意味着关闭）
 */
export function navBackTarget(state) {
    const stack = (state && state.originStack) || [];
    return stack.length ? stack[stack.length - 1] : null;
}

/**
 * 返回一步。
 * @param {{view: string, originStack: string[]}} state
 * @returns {{view: string, originStack: string[]}|null} null = 关闭整个聊天
 */
export function navBack(state) {
    const stack = (state && state.originStack) || [];
    if (!stack.length) return null;
    return { view: stack[stack.length - 1], originStack: stack.slice(0, -1) };
}

/**
 * 平级切换(tab 之间)。**不产生来路。**
 *
 * Chat / Help / Home 是 tab 上的平级目的地,互相之间没有上下级关系。此前 tab
 * 点击走的是 navGo,于是每切一次就往返回栈压一层:知识库 → Chat tab 之后,栈里
 * 留着 KNOWLEDGE,会话里的返回就落到知识库 —— 而用户只是用 tab 换了个地方看。
 * 用户原话:"我们真的要切换不是有 tab 吗"。
 *
 * 目标已在栈里时仍然截断:那是"回到来过的地方",留着会让栈里出现一个已经离开的
 * 视图,后续 navBack 会把人送回刚离开的那一层。
 */
export function navSwitchTab(state, target) {
    const stack = (state && state.originStack) || [];
    const seen = stack.indexOf(target);
    if (seen >= 0) return { view: target, originStack: stack.slice(0, seen) };
    return { view: target, originStack: stack.slice() };
}

/**
 * 左上角那颗按钮的**落点**。
 *
 * 这颗按钮身兼两个唯一通道,所以不能只做一件事:
 *  · Home 默认没有 tab(服务端 show_home_tab 缺省 false,理由正是"左上角常驻
 *    按钮已是导航入口")→ 它是回 Home 的唯一路;
 *  · 会话列表根本没有 tab(tab 只有 know/home/chat,列表是 chat 的子视图)
 *    → 在会话里它是上到列表的唯一路。
 *
 * 规则分两档,**刻意不统一**:
 *
 *  1. 顶层视图(会话列表 / 知识库列表 / 文章)→ 恒定 Home。
 *     它们之间靠 tab 平级切换,彼此没有"上一层";唯一合理的出口就是 Home。
 *     这一档不看来路 —— 用户原话:"只要是会话列表,左上角显示 button 的时候
 *     应该是 home"。
 *
 *  2. 会话内部 → **回到来路**(R18)。这是真正的下钻,而且用户早先明确要求过
 *     "从首页进入的对话,返回应该回到 home"。所以这一档必须看栈,不能像第 1 档
 *     那样固定 —— 从 Home 进来就回 Home,从列表进来就回列表。
 *
 * 栈空时(直接落地进会话)退回会话列表:此时"返回"没有来路,而列表在 Home 被
 * 商户关掉时**完全不可达**(没有 tab、Home 的入口也没了),回访访客的历史会话
 * 会被锁死。给列表而不是不画按钮,补的是这个缺口。
 *
 * 真正被砍掉的是**历史污染**:tab 切换不再压栈(navSwitchTab),所以
 * "知识库 tab → Chat tab" 之后,会话里的来路仍是进面板时那个,不会变成知识库。
 *
 * @param backTarget navBackTarget(nav) 的结果,仅第 2 档使用
 * @returns {string|null} 目标视图,null = 此处无处可去
 */
export function cornerButtonTarget(widgetClass, chatSubview, backTarget) {
    if (widgetClass === 'home-widget') return null;
    if (widgetClass === 'chat-widget' && chatSubview !== 'list') {
        return backTarget || NAV_CHAT_LIST;
    }
    return NAV_HOME;
}
